import { getIdToken } from '@pooflabs/web';
import {
  Cloud,
  CheckCircle2,
  Cpu,
  Gauge,
  HardDrive,
  Loader2,
  MemoryStick,
  Pause,
  Play,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import type { GpuNodesResponse } from '@/lib/collections/gpuNodes';
import { formatSol } from '@/utils/format';
import { computeDisplayUnclaimedBase, formatTHive, getTierMeta, reputationPercent } from '@/utils/gpuNetwork';

interface NodeCardProps {
  node: GpuNodesResponse;
  walletAddress: string;
  tokenReady: boolean;
}

interface ClaimResponse {
  claimId: string;
  nodeId: string;
  amountBase: number;
  amountWhole: number;
}

const TIER_DOT: Record<string, string> = {
  low: 'bg-slate-400',
  mid: 'bg-sky-500',
  high: 'bg-violet-500',
  datacenter: 'bg-[#00C805]',
};

/**
 * All mutating actions (pause/resume/claim) go through the authenticated
 * backend — the gpuNodes policy only allows the treasury vault to update
 * accounting fields, so this card never writes to the collection directly.
 */
export const NodeCard: React.FC<NodeCardProps> = ({ node, walletAddress, tokenReady }) => {
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  const [claiming, setClaiming] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tick every second so the unclaimed counter accrues live while active.
  useEffect(() => {
    const id = setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const tierMeta = getTierMeta(node.tier);
  const isActive = node.status === 'active';
  const unclaimedBase = computeDisplayUnclaimedBase(node, nowSeconds);
  const canClaim = tokenReady && unclaimedBase > 0 && !claiming;
  const busy = claiming || pausing || resuming || deleting;

  const getAuthApi = async () => {
    const token = await getIdToken();
    if (!token) {
      toast.error('Please reconnect your wallet and try again.');
      return null;
    }
    return createAuthenticatedApiClient(token, walletAddress);
  };

  const handlePause = async () => {
    if (busy) return;
    setPausing(true);
    try {
      const authApi = await getAuthApi();
      if (!authApi) return;
      await authApi.post(`/api/nodes/${node.id}/pause`);
      toast.success(`${node.gpuModel} paused — your earnings are banked.`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not pause this node.');
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    if (busy) return;
    setResuming(true);
    try {
      const authApi = await getAuthApi();
      if (!authApi) return;
      await authApi.post(`/api/nodes/${node.id}/resume`);
      toast.success(`${node.gpuModel} is back online.`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not resume this node.');
    } finally {
      setResuming(false);
    }
  };

  const handleClaim = async () => {
    if (busy) return;
    setClaiming(true);
    try {
      const authApi = await getAuthApi();
      if (!authApi) return;
      const result = await authApi.post<ClaimResponse>('/api/nodes/claim', { nodeId: node.id });
      toast.success(`Claimed ${result.amountWhole.toFixed(4)} t-hive from ${node.gpuModel}!`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Claim failed. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setDeleting(true);
    try {
      const authApi = await getAuthApi();
      if (!authApi) return;
      await authApi.post(`/api/nodes/${node.id}/delete`);
      toast.success(`${node.gpuModel} removed from the network.`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not delete this node. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-sm relative overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* subtle scan-line accent */}
      <div
        className="absolute inset-x-0 top-0 h-1 opacity-80"
        style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)' }}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-2xl bg-secondary flex items-center justify-center shrink-0">
            <Cpu className="w-5 h-5 text-foreground" />
          </span>
          <div className="min-w-0">
            <p className="font-extrabold truncate">{node.label || node.gpuModel}</p>
            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold ${
                  isActive ? 'text-[#00C805] bg-[#00C805]/10' : 'text-muted-foreground bg-secondary'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#00C805] animate-pulse' : 'bg-muted-foreground/40'}`} />
                {isActive ? 'Active' : 'Paused'}
              </span>
              <span>·</span>
              {node.serverType === 'aws' ? (
                <span className="inline-flex items-center gap-1">
                  <Cloud className="w-3 h-3" /> AWS {node.awsRegion}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <HardDrive className="w-3 h-3" /> Self-hosted
                </span>
              )}
              <span>·</span>
              <MemoryStick className="w-3 h-3" />
              {node.vram}GB
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${TIER_DOT[node.tier] ?? 'bg-slate-400'}`}
          >
            {tierMeta.label}
          </span>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                disabled={busy}
                aria-label="Delete node"
                className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {node.label || node.gpuModel}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the node from the registry and stops all earning.
                  {node.isRented
                    ? ' It currently has an active rental — we\u2019ll settle and pay out the accrued SOL to you first.'
                    : ''}{' '}
                  This can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete node
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {node.label && <p className="text-[11px] text-muted-foreground mt-2 truncate">{node.gpuModel}</p>}

      <div className="grid grid-cols-2 gap-2.5 mt-4">
        <div className="rounded-2xl bg-secondary/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Zap className="w-3 h-3" /> Rate
          </p>
          <p className="text-sm font-extrabold mt-0.5">{node.ratePer30s} t-hive / 30s</p>
        </div>
        <div className="rounded-2xl bg-secondary/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Gauge className="w-3 h-3" /> Total earned
          </p>
          <p className="text-sm font-extrabold mt-0.5">{formatTHive(node.totalEarned, 2)} t-hive</p>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-primary/30 bg-accent/60 p-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-foreground/70">Unclaimed t-hive</p>
        <p className="text-2xl font-extrabold tabular-nums tracking-tight text-accent-foreground mt-0.5">
          {formatTHive(unclaimedBase, 4)}
        </p>
      </div>

      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          <Star className="w-3 h-3 fill-current text-amber-400" /> {reputationPercent(node.reputationScore)}% rep
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          <Gauge className="w-3 h-3" /> {node.uptimePercent ?? 100}% uptime
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          <CheckCircle2 className="w-3 h-3" /> {node.completedJobs ?? 0} jobs
        </span>
      </div>

      <div className="mt-2">
        {node.isRented ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold bg-[#00C805]/10 text-[#00C805]">
            <Zap className="w-3 h-3" /> Rented — earning SOL
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-secondary text-muted-foreground">
            Listed for rent at {formatSol(node.rentPricePerHourLamports, 4)}/hr
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mt-4">
        {isActive ? (
          <button
            onClick={handlePause}
            disabled={busy}
            className="flex-1 rounded-full border border-input font-semibold py-2.5 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {pausing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
            Pause
          </button>
        ) : (
          <button
            onClick={handleResume}
            disabled={busy}
            className="flex-1 rounded-full border border-input font-semibold py-2.5 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {resuming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Resume
          </button>
        )}
        <button
          onClick={handleClaim}
          disabled={!canClaim || busy}
          className="flex-1 rounded-full bg-[#00C805] text-white font-bold py-2.5 text-sm shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-40 disabled:shadow-none flex items-center justify-center gap-1.5"
        >
          {claiming && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {!tokenReady ? 'Initializing…' : claiming ? 'Claiming…' : 'Claim'}
        </button>
      </div>
    </div>
  );
};

export default NodeCard;
